import { useApolloClient } from '@apollo/client';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { evictField } from '@/utils/graphqlUtils';

export interface ResetTabPreferencesTRPCInput {
  ObjectType: Parent_Type_Enum;
}

/**
 * Maps the TRPC mutation response to match the GraphQL mutation
 * structure (delete_organisation_tab_preference / delete_user_tab_preference
 * each returning `{ affected_rows }`).
 */
const mapTrpcResponseToGraphQL = (data: {
  organisationAffected: number;
  userAffected: number;
}) => ({
  delete_organisation_tab_preference: {
    __typename: 'organisation_tab_preference_mutation_response' as const,
    affected_rows: data.organisationAffected,
  },
  delete_user_tab_preference: {
    __typename: 'user_tab_preference_mutation_response' as const,
    affected_rows: data.userAffected,
  },
});

export const useResetTabPreferencesTRPC = () => {
  const trpc = useTRPC();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.userTabPreference.resetTabPreferences.mutationOptions({
      onSuccess: async () => {
        // Mirror the legacy GraphQL `update` callback so Apollo readers
        // refresh after the reset. Both tab-preference tables are still
        // read via Apollo, so eviction stays in lockstep with the old
        // mutation's behaviour.
        evictField(apolloClient.cache, 'organisation_tab_preference');
        evictField(apolloClient.cache, 'user_tab_preference');
        // COI-889: `useGetDefaultTabs` reads the preference rows through
        // `frontend.tab.defaults` (5-minute window), not the Apollo leg.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.tab.pathKey(),
        });
      },
    }),
  });

  return {
    resetTabPreferences: async (variables: ResetTabPreferencesTRPCInput) => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
